import { LoadingButton } from '@mui/lab';
import { Box, Stack, TextField, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import { ja } from 'date-fns/locale/ja';
import { format } from 'date-fns-tz';
import { useSnackbar } from 'notistack';
import { ChangeEvent, FC, memo, useContext, useEffect, useState } from 'react';
import LicenseContext from 'src/contexts/LicenseContext';
import useImport from 'src/hooks/useImport';
import { queryKey } from 'src/hooks/useOrders.request';
import { useMutationMappingRules } from 'src/hooks/useQueryMappingRules';
import saveAs from 'src/utils/fileSaver';
import requestUtil from 'src/utils/request.util';

import { fiveZeroZeroErrorMessage } from '../../constants/messages';

import Header from './Header';
import { markImportRequested, releaseImportRequest } from './importRequestGuard';

const Process: FC = memo(() => {
  const importContext = useImport();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const licenseContext = useContext(LicenseContext);
  const { updateMappingRule } = useMutationMappingRules();

  const buildRuleName = () => {
    const today = format(new Date(), 'yyyy-MM-dd', { locale: ja });
    const randomNumber = `${Math.floor(Math.random() * 100000) + 1}`;

    return [today, randomNumber].join('-');
  };

  const [importLength, setImportLength] = useState<undefined | number>(undefined);
  const [ruleIsSaved, setRuleIsSaved] = useState<boolean>(false);
  const [ruleName, setRuleName] = useState<string>(buildRuleName());
  const [isSameMappingRule, setIsSameMappingRule] = useState<boolean>(false);

  const textFieldOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setRuleName(event.target.value);
  };

  const [existSameMappingName, setExistSameMappingName] = useState<boolean>(false);

  const importRequest = () => {
    importContext.setIsLoading(true);

    if (importContext.selectedRuleId !== -1) {
      updateMappingRule.mutate(importContext.getMappingRule());
    }

    const requestPath = `/api/v3/orders/imports/${importContext.importId}`;
    axios.patch(requestPath, {
      skip_mapping: importContext.skipMapping.toString(),
      shipper_id: importContext.shipperId,
      shipper_name: importContext.shipperName,
      loading_place_id: importContext.loadingPlaceId,
      unloading_place_id: importContext.unloadingPlaceId,
      weight_factor: importContext.weightFactor,
      volume_factor: importContext.volumeFactor,
      duplicate_diff_action: importContext.duplicateDiffAction,
    })
      .then((response: AxiosResponse<number>) => {
        setImportLength(response.data);
        licenseContext.updateAsyncJobRequested(true);
      })
      .catch((e) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (e.response?.status === 400) {
          // 取込 API の 400 はジョブ登録前のバリデーションエラーだけなので、
          // 取り込まれていないことが確実。記録を取り消して再送できるようにする
          releaseImportRequest(importContext.importId);
          enqueueSnackbar('取り込めない案件情報がありました。データをご確認ください。');
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
          const blob = new Blob([e.response.data], { type: e.response.data.type });
          saveAs(blob, 'errors.txt');
        } else {
          // 500 や通信失敗はサーバーがジョブを登録済みの可能性があり、
          // 再送すると 2 本目の取込ジョブが走るため記録は残す
          enqueueSnackbar(fiveZeroZeroErrorMessage);
        }
        // ここで再 throw しても受け手がなく unhandled rejection になるだけなので投げ直さない。
        // ネットワークエラー（e.response が無い）でも上のメッセージ表示まで到達させる
      })
      .finally(() => {
        void queryClient.invalidateQueries({
          queryKey: ['dashboards']
        });
        void queryClient.resetQueries({
          queryKey: ['orders']
        });
        void queryClient.resetQueries({
          queryKey: [queryKey]
        });
        importContext.setIsLoading(false);
      });
  };

  const upsertMappingRule = () => {
    const requestPath = '/api/v3/orders/mappings';
    const requestBody = { ...importContext.getMappingRule(), name: ruleName, };

    importContext.setIsLoading(true);

    axios
      .post(requestPath, requestUtil.convertKeysToSnakeCase(requestBody))
      .then(() => {
        enqueueSnackbar(`${ruleName}を保存しました`);
        setRuleIsSaved(true);
      })
      .catch((e) => {
        enqueueSnackbar(fiveZeroZeroErrorMessage);
        throw e;
      })
      .finally(() => {
        importContext.setIsLoading(false);
      });
  };

  useEffect(() => {
    if (importContext.importId && markImportRequested(importContext.importId)) {
      importRequest();
    }

    const currentMappingRule = importContext.mappings;
    const selectedMappingRule = importContext.mappingRules.find((rule) => rule.id === importContext.selectedRuleId)?.mapping;
    const isSame = selectedMappingRule
      ? Object.keys(currentMappingRule).every((key) => JSON.stringify(currentMappingRule[key]) === JSON.stringify(selectedMappingRule[key]))
      : false;
    setIsSameMappingRule(isSame);
  }, []);

  useEffect(() => {
    setExistSameMappingName(importContext.mappingRules.map((rule) => rule.name).includes(ruleName));
  }, [ruleName]);

  useEffect(() => {
    const maybeSelectedRuleName = importContext.mappingRules.find((rule) => rule.id === importContext.selectedRuleId)?.name;

    if (!maybeSelectedRuleName) return;

    setRuleName(maybeSelectedRuleName);
  }, [importContext.selectedRuleId]);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box>
        <Header step={4} />
      </Box>
      <Box
        flexGrow={1}
        paddingX={2}
        paddingY={2}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <img
          src="/static/images/import_done.png"
          alt="import_csv"
          height={172}
        />
        <p>
          インポートを開始しました。終了次第、案件一覧に反映されます。
        </p>

        {[ruleIsSaved, isSameMappingRule, importContext.isDefaultMappings()].every((bool) => !bool) && (
          <Stack spacing={1}>
            <Typography
              textAlign="center"
              variant="h5"
            >
              このルールを保存しますか？
            </Typography>
            {existSameMappingName && (
              <Stack textAlign="center">
                <Typography
                  variant="caption"
                >
                  {ruleName}
                  は、既に設定されています。
                </Typography>
                <Typography
                  variant="caption"
                >
                  別名で保存する場合は、名前を変更してください。
                </Typography>
              </Stack>
            )}
            <Stack direction="row" gap={1}>
              <TextField
                onChange={textFieldOnChange}
                value={ruleName}
              />
              <LoadingButton
                loading={importContext.isLoading}
                variant="outlined"
                onClick={upsertMappingRule}
              >
                {existSameMappingName
                  ? '上書き保存する'
                  : '保存する'}
              </LoadingButton>
            </Stack>
          </Stack>
        )}
      </Box>
    </Box>
  );
});

export default Process;
